// src/pages/Support.js - Enhanced with Quick Responses
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function Support() {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  // Quick Responses
  const quickResponses = [
    { id: 1, text: "Thank you for contacting support. I'll look into this right away and get back to you shortly.", icon: '🔍' },
    { id: 2, text: "I understand your concern. Let me escalate this to the appropriate team for urgent review.", icon: '📤' },
    { id: 3, text: "This issue has been resolved. Please confirm if everything is working properly now.", icon: '✅' },
    { id: 4, text: "I'll need some more information to help you better. Could you please provide additional details?", icon: '📝' },
    { id: 5, text: "We're currently investigating this issue. We'll update you as soon as we have more information.", icon: '🔬' },
    { id: 6, text: "Thank you for your patience. We're working on resolving this as quickly as possible.", icon: '🙏' },
    { id: 7, text: "I've reviewed your request and it's been forwarded to the appropriate team for processing.", icon: '📋' },
    { id: 8, text: "Please try restarting the app and clearing your cache. Let me know if the issue persists.", icon: '🔄' },
  ];

  // Ticket Templates
  const ticketTemplates = [
    { id: 1, subject: 'Ride Issue', message: 'I had an issue with my recent ride. ' },
    { id: 2, subject: 'Payment Problem', message: 'I was charged incorrectly for my ride. ' },
    { id: 3, subject: 'Driver Complaint', message: 'I would like to report an issue with my driver. ' },
    { id: 4, subject: 'App Technical Issue', message: 'The app is not working properly. ' },
    { id: 5, subject: 'Account Issue', message: 'I\'m having trouble with my account. ' },
  ];

  useEffect(() => {
    fetchTickets();
    const interval = setInterval(fetchTickets, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedTicket) {
      fetchTicketDetails(selectedTicket.id);
    }
  }, [selectedTicket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/support/tickets`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      const ticketData = response.data.tickets || [];
      setTickets(ticketData);
      
      if (selectedTicket) {
        const updated = ticketData.find(t => t.id === selectedTicket.id);
        if (updated) {
          setSelectedTicket(updated);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/support/tickets/${ticketId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    }
  };

  const updateTicketStatus = async (ticketId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/support/tickets/${ticketId}/status`, { status }, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      setSelectedTicket({ ...selectedTicket, status });
      fetchTickets();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update ticket status');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket || sending) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/support/messages`, {
        ticket_id: selectedTicket.id,
        sender_id: 1,
        message: newMessage.trim(),
      }, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.data.success) {
        setNewMessage('');
        await fetchTicketDetails(selectedTicket.id);
        await fetchTickets();
        setShowQuickReplies(false);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const goToUserProfile = (userId, userType) => {
    if (userType === 'driver') {
      navigate(`/drivers/${userId}`);
    } else {
      navigate(`/customers/${userId}`);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: { bg: '#dcfce7', color: '#166534', label: 'Open', icon: '🟢' },
      in_progress: { bg: '#fef3c7', color: '#92400e', label: 'In Progress', icon: '🟡' },
      resolved: { bg: '#dbeafe', color: '#1e40af', label: 'Resolved', icon: '🔵' },
      closed: { bg: '#f1f5f9', color: '#64748b', label: 'Closed', icon: '⚪' },
    };
    return colors[status] || colors.open;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: { bg: '#fee2e2', color: '#991b1b', label: '🔴 High' },
      medium: { bg: '#fef3c7', color: '#92400e', label: '🟡 Medium' },
      low: { bg: '#dcfce7', color: '#166534', label: '🟢 Low' },
    };
    return colors[priority] || colors.medium;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return date.toLocaleDateString();
  };

  const getFilteredTickets = () => {
    let filtered = tickets;
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(t => t.status === filterStatus);
    }
    
    if (filterPriority !== 'all') {
      filtered = filtered.filter(t => t.priority === filterPriority);
    }
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(t => 
        t.subject?.toLowerCase().includes(term) ||
        t.user_name?.toLowerCase().includes(term) ||
        t.ticket_number?.toLowerCase().includes(term) ||
        t.user_phone?.includes(term)
      );
    }
    
    return filtered;
  };

  const filteredTickets = getFilteredTickets();
  const statusCounts = {
    all: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    in_progress: tickets.filter(t => t.status === 'in_progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    closed: tickets.filter(t => t.status === 'closed').length,
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f1f5f9'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>💬</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading conversations...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      height: 'calc(100vh - 80px)', 
      display: 'flex', 
      gap: '20px',
      backgroundColor: '#f1f5f9',
    }}>
      {/* Left Panel - Ticket List */}
      <div style={{
        width: '400px',
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f8fafc',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
                💬 Support Tickets
              </h3>
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                {tickets.length} total • {statusCounts.open} open
              </span>
            </div>
            <button
              onClick={fetchTickets}
              style={{
                padding: '6px 12px',
                backgroundColor: '#f97316',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              🔄
            </button>
          </div>
          
          {/* Search */}
          <div style={{ marginTop: '12px', position: 'relative' }}>
            <input
              type="text"
              placeholder="🔍 Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                fontSize: '14px',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
          
          {/* Status Filters */}
          <div style={{ 
            display: 'flex', 
            gap: '6px', 
            marginTop: '10px',
            flexWrap: 'wrap',
          }}>
            {['all', 'open', 'in_progress', 'resolved', 'closed'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '11px',
                  border: filterStatus === status ? '2px solid #f97316' : '1px solid #e2e8f0',
                  backgroundColor: filterStatus === status ? '#f97316' : 'transparent',
                  color: filterStatus === status ? 'white' : '#64748b',
                  cursor: 'pointer',
                  fontWeight: filterStatus === status ? '600' : '400',
                }}
              >
                {status === 'all' ? 'All' : status.replace('_', ' ')}
                <span style={{ 
                  marginLeft: '4px',
                  fontSize: '10px',
                  opacity: 0.7,
                }}>
                  ({statusCounts[status] || 0})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Ticket List */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto',
          padding: '8px 0',
        }}>
          {filteredTickets.length === 0 ? (
            <div style={{ 
              padding: '40px 20px', 
              textAlign: 'center', 
              color: '#94a3b8' 
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
              <p style={{ margin: 0 }}>
                {searchTerm ? 'No tickets match your search' : 'No support tickets yet'}
              </p>
            </div>
          ) : (
            filteredTickets.map((ticket) => {
              const status = getStatusColor(ticket.status);
              const isSelected = selectedTicket?.id === ticket.id;
              const unreadCount = ticket.unread_count || 0;
              
              return (
                <div
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  style={{
                    padding: '14px 20px',
                    cursor: 'pointer',
                    backgroundColor: isSelected ? '#fef3c7' : 'transparent',
                    borderLeft: isSelected ? '3px solid #f97316' : '3px solid transparent',
                    borderBottom: '1px solid #f1f5f9',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontWeight: '600', 
                        fontSize: '14px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {ticket.subject || 'No Subject'}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#64748b',
                        marginTop: '2px',
                      }}>
                        #{ticket.ticket_number || ticket.id} • 
                        <span 
                          style={{ 
                            cursor: 'pointer', 
                            color: '#3b82f6',
                            textDecoration: 'underline',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            goToUserProfile(ticket.user_id, ticket.user_type);
                          }}
                        >
                          {ticket.user_name || 'Unknown'}
                        </span>
                      </div>
                      {ticket.last_message && (
                        <div style={{
                          fontSize: '12px',
                          color: '#94a3b8',
                          marginTop: '4px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {ticket.last_message.substring(0, 60)}
                          {ticket.last_message.length > 60 ? '...' : ''}
                        </div>
                      )}
                    </div>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'flex-end',
                      gap: '4px',
                      flexShrink: 0,
                      marginLeft: '12px',
                    }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 10px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        backgroundColor: status.bg,
                        color: status.color,
                        fontWeight: '500',
                      }}>
                        {status.icon} {status.label}
                      </span>
                      {unreadCount > 0 && (
                        <span style={{
                          backgroundColor: '#ef4444',
                          color: 'white',
                          borderRadius: '50%',
                          minWidth: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          padding: '0 6px',
                        }}>
                          {unreadCount}
                        </span>
                      )}
                      <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                        {formatDate(ticket.last_activity || ticket.created_at)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right Panel - Chat Area */}
      <div style={{
        flex: 1,
        backgroundColor: 'white',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {!selectedTicket ? (
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center',
            color: '#94a3b8',
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>💬</div>
            <h3 style={{ margin: 0, color: '#64748b' }}>Select a conversation</h3>
            <p style={{ fontSize: '14px', marginTop: '8px' }}>
              Choose a ticket from the left panel to start chatting
            </p>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px',
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: '#f97316',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                  }}
                  onClick={() => goToUserProfile(selectedTicket.user_id, selectedTicket.user_type)}
                  >
                    {selectedTicket.user_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <div style={{ 
                      fontWeight: 'bold', 
                      fontSize: '16px',
                      cursor: 'pointer',
                      color: '#3b82f6',
                      textDecoration: 'underline',
                    }}
                    onClick={() => goToUserProfile(selectedTicket.user_id, selectedTicket.user_type)}
                    >
                      {selectedTicket.user_name || 'Unknown User'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      #{selectedTicket.ticket_number || selectedTicket.id} • {selectedTicket.user_phone || 'No phone'}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => goToUserProfile(selectedTicket.user_id, selectedTicket.user_type)}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  👤 View Profile
                </button>
                <select
                  value={selectedTicket.status}
                  onChange={(e) => updateTicketStatus(selectedTicket.id, e.target.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    outline: 'none',
                  }}
                >
                  <option value="open">🟢 Open</option>
                  <option value="in_progress">🟡 In Progress</option>
                  <option value="resolved">🔵 Resolved</option>
                  <option value="closed">⚪ Closed</option>
                </select>
              </div>
            </div>

            {/* Messages */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto', 
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              backgroundColor: '#fafbfc',
            }}>
              {messages.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#94a3b8', 
                  padding: '40px 0',
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px' }}>✉️</div>
                  <p style={{ margin: 0 }}>No messages yet</p>
                  <p style={{ fontSize: '13px', marginTop: '4px' }}>
                    Start the conversation by sending a message below
                  </p>
                </div>
              ) : (
                messages.map((msg, index) => {
                  const isAdmin = msg.sender_type === 'admin';
                  const showDate = index === 0 || 
                    new Date(msg.created_at).toDateString() !== 
                    new Date(messages[index - 1]?.created_at).toDateString();
                  
                  return (
                    <div key={msg.id}>
                      {showDate && (
                        <div style={{
                          textAlign: 'center',
                          fontSize: '11px',
                          color: '#94a3b8',
                          padding: '8px 0',
                        }}>
                          {new Date(msg.created_at).toLocaleDateString('en', {
                            weekday: 'long',
                            month: 'long',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </div>
                      )}
                      <div style={{
                        display: 'flex',
                        justifyContent: isAdmin ? 'flex-end' : 'flex-start',
                        marginBottom: '4px',
                      }}>
                        <div style={{
                          maxWidth: '75%',
                          backgroundColor: isAdmin ? '#f97316' : 'white',
                          color: isAdmin ? 'white' : '#1e293b',
                          padding: '10px 16px',
                          borderRadius: isAdmin ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          boxShadow: isAdmin ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
                          border: isAdmin ? 'none' : '1px solid #e2e8f0',
                          wordBreak: 'break-word',
                        }}>
                          {!isAdmin && msg.sender_name && (
                            <div style={{
                              fontSize: '11px',
                              fontWeight: '600',
                              color: '#3b82f6',
                              marginBottom: '4px',
                            }}>
                              {msg.sender_name}
                            </div>
                          )}
                          <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                            {msg.message}
                          </div>
                          <div style={{
                            fontSize: '10px',
                            marginTop: '4px',
                            color: isAdmin ? 'rgba(255,255,255,0.7)' : '#94a3b8',
                            textAlign: 'right',
                          }}>
                            {formatDate(msg.created_at)}
                            {isAdmin && ' ✓'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick Responses Bar */}
            <div style={{
              padding: '8px 16px',
              borderTop: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc',
              display: 'flex',
              gap: '6px',
              flexWrap: 'wrap',
              alignItems: 'center',
              maxHeight: showQuickReplies ? '120px' : '48px',
              overflow: 'auto',
              transition: 'max-height 0.3s ease',
            }}>
              <button
                onClick={() => setShowQuickReplies(!showQuickReplies)}
                style={{
                  padding: '4px 10px',
                  backgroundColor: showQuickReplies ? '#f97316' : 'transparent',
                  color: showQuickReplies ? 'white' : '#64748b',
                  border: showQuickReplies ? 'none' : '1px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  fontWeight: showQuickReplies ? '600' : '400',
                }}
              >
                {showQuickReplies ? '✕ Hide' : '⚡ Quick Responses'}
              </button>
              
              {showQuickReplies && quickResponses.map((reply) => (
                <button
                  key={reply.id}
                  onClick={() => {
                    setNewMessage(reply.text);
                    setShowQuickReplies(false);
                  }}
                  style={{
                    padding: '4px 10px',
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    color: '#475569',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f97316';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.borderColor = '#f97316';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.color = '#475569';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                >
                  {reply.icon} {reply.text.length > 25 ? reply.text.substring(0, 25) + '...' : reply.text}
                </button>
              ))}
            </div>

            {/* Message Input */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e2e8f0',
              backgroundColor: 'white',
            }}>
              <div style={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-end',
              }}>
                <div style={{
                  flex: 1,
                  position: 'relative',
                }}>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your message..."
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      resize: 'none',
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#f97316'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  />
                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '12px',
                    fontSize: '11px',
                    color: '#94a3b8',
                  }}>
                    {newMessage.length > 0 && `Press Enter to send`}
                  </div>
                </div>
                <button
                  onClick={sendMessage}
                  disabled={sending || !newMessage.trim()}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#f97316',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: sending || !newMessage.trim() ? 'not-allowed' : 'pointer',
                    opacity: sending || !newMessage.trim() ? 0.6 : 1,
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    height: '48px',
                  }}
                >
                  {sending ? 'Sending...' : 'Send ✈️'}
                </button>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '8px',
              }}>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                  💡 Press Enter to send • Shift+Enter for new line
                </span>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {messages.length} messages
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Support;